import { filter, get, shuffle } from 'lodash';
import avo from 'analytics/avo';
import isClient from 'commons/isClient';
import device from '../lib/device';
import getCookies from './getCookies';
import isServer from './isServer';
import getAdminBackendUrl from './getAdminBackendUrl';
import getHttpContextValue from './getHttpContextValue';
import { sendGAExperimentData } from './analytics';
import { HTTP_CONTEXT_FIELDS } from './constants';

const AB_TEST_COOKIE = 'AB4';

let currentExperiments = [];
let serverCookie = {};

const setABCookie = value => {
  if (isServer()) {
    serverCookie = value;
    return;
  }
  const newValue = Object.keys(value)
    .map(key => ({
      key,
      v: value[key].variation ?? value[key].v,
      a: value[key].analytics ?? value[key].a,
    }))
    .reduce((acc, val) => {
      acc[val.key] = val;
      delete val.key;
      return acc;
    }, {});
  getCookies().set(AB_TEST_COOKIE, JSON.stringify(newValue), {
    path: '/',
    maxAge: 31536000, // 1 year
  });
};

const getABCookie = () => {
  if (isServer()) {
    return serverCookie;
  }
  let cookieValue = {};
  try {
    const cookieValueRaw = getCookies().get(AB_TEST_COOKIE);
    if (typeof cookieValueRaw === 'string' && cookieValueRaw.length > 0) {
      cookieValue = JSON.parse(cookieValue);
    } else {
      cookieValue = cookieValueRaw || {};
    }
    cookieValue = Object.keys(cookieValue)
      .map(key => ({
        key,
        variation: cookieValue[key].variation ?? cookieValue[key].v,
        analytics: cookieValue[key].analytics ?? cookieValue[key].a,
      }))
      .reduce((acc, val) => {
        acc[val.key] = val;
        delete val.key;
        return acc;
      }, {});
    return cookieValue;
  } catch (err) {
    // Ignore errors
  }
  return cookieValue;
};

export const migrateAB4CookieToNewSchema = () => {
  const cookie = getABCookie();
  if (!cookie || !Object.keys(cookie).length) {
    return;
  }
  const experimentIds = Object.keys(cookie);
  if (typeof cookie[experimentIds[0]] === 'object') {
    // already migrated
    return;
  }

  const newCookie = Object.keys(cookie).reduce((acc, experimentId) => {
    acc[experimentId] = { variation: cookie[experimentId], analytics: true };
    return acc;
  }, {});
  setABCookie(newCookie);
};

const deleteInActiveExperimentData = experiments => {
  const cookie = getABCookie();
  if (!cookie || !Object.keys(cookie).length) {
    return;
  }
  const experimentIds = Object.keys(cookie);
  const newCookie = { ...cookie };
  const toBeDeleted = experimentIds.filter(
    experimentId =>
      !experiments.find(
        ({ optimizeExperimentId }) => optimizeExperimentId === experimentId
      )
  );
  if (!toBeDeleted.length) {
    return;
  }
  toBeDeleted.forEach(experimentId => delete newCookie[experimentId]);
  setABCookie(newCookie);
};

const sendStatToAdmin = async (experimentId, variationId) => {
  const adminAPIUrl = getAdminBackendUrl();
  if (!adminAPIUrl) {
    return;
  }
  await fetch(adminAPIUrl, {
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      operationName: null,
      variables: {},
      query: `mutation { 
        saveABTestStat(stat: {experimentId: "${experimentId}", variationId: "${variationId}"}) 
      }`,
    }),
    method: 'POST',
  });
};

const getExperimentNameByExperimentId = experimentId =>
  currentExperiments[
    Object.keys(currentExperiments).find(key => {
      const config = currentExperiments[key];
      return config && config.optimizeExperimentId === experimentId;
    })
  ]?.name || '';

const trackVariation = (experimentId, variationId) => {
  if (isClient()) {
    const cookie = getABCookie();
    // 4.0 Admin
    sendStatToAdmin(experimentId, variationId);
    // GA
    sendGAExperimentData(experimentId, variationId);
    // Avo
    avo.experimentStarted({
      experimentGroup: variationId,
      experimentId,
      experimentName: getExperimentNameByExperimentId(experimentId),
      experimentTrackingRoute: window.location.pathname,
    });
    if (cookie[experimentId]) {
      cookie[experimentId].analytics = true;
      setABCookie(cookie);
    }
  }
};

const syncAnalytics = () => {
  const cookieValue = getABCookie();
  Object.keys(cookieValue).forEach(experimentId => {
    if (cookieValue[experimentId].analytics) {
      return;
    }
    trackVariation(experimentId, cookieValue[experimentId].variation);
  });
};

const getQueryParamExperimentOverrides = () => {
  if (isServer()) {
    return null;
  }

  try {
    const overrideString = new URLSearchParams(window.location.search).get(
      'ABTestOverrides'
    );
    return JSON.parse(overrideString);
  } catch (err) {
    /* ignore */
    return null;
  }
};

const isExperimentEligible = experiment => {
  // Filter non eligible experiments
  const allowedDeviceTypes = get(experiment, 'eligibility.device.allowedTypes');
  if (!allowedDeviceTypes || allowedDeviceTypes.length === 0) {
    return true;
  }
  // Filter the experiments based on the device characteristics.
  let deviceType = getHttpContextValue(HTTP_CONTEXT_FIELDS.REQ_DEVICE_TYPE);
  if (!deviceType && isClient()) {
    deviceType = device(window.navigator.userAgent).type;
  }
  if (!deviceType) {
    // Consider eligible if the device type cannot be identified
    return true;
  }
  return allowedDeviceTypes.indexOf(deviceType) > -1;
};

const findVariationWithMajorityTraffic = variations => {
  let choosenVariation = variations[0];
  variations.forEach(variation => {
    if (variation.trafficPercent > choosenVariation.trafficPercent) {
      choosenVariation = variation;
    }
  });
  return choosenVariation;
};

const initializeNumberQueue = () => {
  const numbers = [];
  for (let i = 1; i <= 100; i++) {
    numbers.push(i);
  }
  return shuffle(numbers);
};

const getVariationFromNumber = (number, variations) =>
  variations.filter(
    variation => variation.minValue <= number && variation.maxValue >= number
  )[0];

const chooseRandomVariation = (experiment, overridenVariationId) => {
  if (isServer()) {
    return findVariationWithMajorityTraffic(experiment.variations);
  }
  if (overridenVariationId) {
    const overridenVariation = experiment.variations.filter(
      variation => +variation.optimizeVariationId === +overridenVariationId
    )[0];
    if (overridenVariation) {
      return overridenVariation;
    }
  }
  if (!experiment.numberQueue || experiment.numberQueue.length === 0) {
    experiment.numberQueue = initializeNumberQueue();
  }
  const random = experiment.numberQueue.shift();
  const chosenVariation = getVariationFromNumber(random, experiment.variations);
  return chosenVariation;
};

export const getVariationNameFromOptimizeVariationId = (
  experimentName,
  optimizeVariationId
) => {
  const experiment = currentExperiments[experimentName];
  if (!experiment) {
    return null;
  }
  const variation = filter(experiment.variations, { optimizeVariationId })[0];
  return variation?.name;
};

export const setChosenVariation = (experimentName, variationName) => {
  const experiment = currentExperiments[experimentName];
  if (!experiment) {
    return;
  }
  const variation = filter(experiment.variations, { name: variationName })[0];
  if (!variation) {
    return;
  }
  const cookieValue = getABCookie();
  if (
    cookieValue[experiment.optimizeExperimentId] &&
    cookieValue[experiment.optimizeExperimentId].variation ===
      variation.optimizeVariationId
  ) {
    return;
  }
  cookieValue[experiment.optimizeExperimentId] = {
    variation: variation.optimizeVariationId,
    analytics: false,
  };
  setABCookie(cookieValue);
  trackVariation(
    experiment.optimizeExperimentId,
    variation.optimizeVariationId
  );
};

export const initializeAllExperiments = overrides => {
  const cookieValue = getABCookie();
  const queryParamOverrides =
    getQueryParamExperimentOverrides() || overrides || {};
  Object.keys(currentExperiments).forEach(name => {
    const experiment = currentExperiments[name];
    if (
      (cookieValue[experiment.optimizeExperimentId] ||
        !isExperimentEligible(experiment) ||
        experiment.lazyInit) &&
      queryParamOverrides[name] == null
    ) {
      // already initialized.
      return;
    }
    const chosenVariation = chooseRandomVariation(
      experiment,
      queryParamOverrides[name]
    );
    setChosenVariation(experiment.name, chosenVariation.name);
  });
};

export const initConfig = (
  experiments = [],
  doInitialize = true,
  variationOverrides
) => {
  migrateAB4CookieToNewSchema();
  deleteInActiveExperimentData(experiments);
  if (isClient()) {
    syncAnalytics();
  }
  /*
    ### Sample experiments config
    [
      {
          "name":"categoryPageTest",
          "optimizeExperimentId":"1234565758696",
          "eligibility": {device: {allowedTypes: ["phone","tablet"]}},
          "variations":[
            {
                "name":"default",
                "matchCriteria":{
                  "name":"Default"
                },
                "trafficPercent":95,
                "optimizeVariationId":0
            },
            {
                "name":"dynamic",
                "matchCriteria":{
                  "id":"5dba7d2c0e87bef25986b79b"
                },
                "trafficPercent":5,
                "optimizeVariationId":1
            }
          ]
      }
    ]
  */
  currentExperiments = experiments.reduce((accumulator, exp) => {
    let percentCounter = 0;
    // Sets the min and max vaue to match from the random number for each variant
    exp.variations = exp.variations.map(eachVariation => {
      const variation = { ...eachVariation };
      variation.minValue = percentCounter;
      variation.maxValue = percentCounter + variation.trafficPercent;
      percentCounter = variation.maxValue + 1;
      return variation;
    });
    accumulator[exp.name] = exp;
    return accumulator;
  }, {});

  if (doInitialize) {
    initializeAllExperiments(variationOverrides);
  }
};

const getVariationFromOptimizeVariationId = (variations, optimizeVariationId) =>
  variations.filter(
    variation => +variation.optimizeVariationId === +optimizeVariationId
  )[0];

export const getCookieFromExperimentName = testName => {
  const experiment = currentExperiments[testName];
  if (!experiment) {
    return null;
  }
  const cookieValue = getABCookie();
  return cookieValue[experiment.optimizeExperimentId];
};

export const getActiveVariation = testName => {
  const experiment = currentExperiments[testName];
  if (!experiment || !isExperimentEligible(experiment)) {
    return null;
  }

  // If variation data in available in cookie use that variation
  const cookieValue = getABCookie();
  if (cookieValue[experiment.optimizeExperimentId]) {
    const alreadyUsedVariation = getVariationFromOptimizeVariationId(
      experiment.variations,
      cookieValue[experiment.optimizeExperimentId].variation
    );
    return alreadyUsedVariation;
  }

  const chosenVariation = chooseRandomVariation(
    experiment,
    getQueryParamExperimentOverrides() || {}
  );
  setChosenVariation(testName, chosenVariation.name);

  return chosenVariation;
};

export const getActiveVariantName = testName => {
  const { name } = getActiveVariation(testName) || {};
  return name;
};

export const getActiveVariationOptimizeId = testName => {
  const { optimizeVariationId } = getActiveVariation(testName) || {};
  return optimizeVariationId;
};

const getExperimentForRouteId = routeId =>
  currentExperiments[
    Object.keys(currentExperiments).find(key => {
      const config = currentExperiments[key];
      return config.isRouteTest && config.routeId === routeId;
    })
  ];

export const isExperimentActiveForRoute = routeId =>
  !!getExperimentForRouteId(routeId);

export const forceVariation = (testName, variationId) => {
  const experiment = currentExperiments[testName];
  if (!experiment) {
    return null;
  }

  const chosenVariation = getVariationFromOptimizeVariationId(
    experiment.variations,
    variationId
  );

  setChosenVariation(testName, chosenVariation.name);
  return null;
};

export const getABRouteVariant = (routeId, routeVariants = []) => {
  const experiment = getExperimentForRouteId(routeId);
  if (!experiment || !isExperimentEligible(experiment)) {
    return null;
  }

  // If variation data in available in cookie use that variation
  const cookieValue = getABCookie();
  if (cookieValue[experiment.optimizeExperimentId]) {
    const alreadyUsedVariation = getVariationFromOptimizeVariationId(
      experiment.variations,
      cookieValue[experiment.optimizeExperimentId].variation
    );
    const chosenRouteVariant = filter(
      routeVariants,
      alreadyUsedVariation.matchCriteria
    )[0];
    if (chosenRouteVariant) {
      return chosenRouteVariant;
    }
  }
  // Else choose a random variation
  const chosenVariation = chooseRandomVariation(experiment);
  if (!chosenVariation) {
    return null;
  }
  const chosenRouteVariant = filter(
    routeVariants,
    chosenVariation.matchCriteria
  )[0];
  if (!chosenRouteVariant) {
    return null;
  }
  setChosenVariation(experiment.name, chosenVariation.name);

  return chosenRouteVariant;
};
